import { createFileRoute, Link } from '@tanstack/react-router'
import { X } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { Button } from '@/components/ui/button'
import { DefinitionBasicDataCreateStep } from '@/features/projects/components/definition-basic-data-step'
import { DefinitionFlowChoiceStep } from '@/features/projects/components/definition-flow-choice-step'
import { DefinitionFlowBackButton, DefinitionFlowFrame } from '@/features/projects/components/definition-flow-frame'
import {
	getDefinitionFlowProgress,
	getNextDefinitionFlowStep,
	resolveDefinitionEntryStep,
} from '@/features/projects/utils/definition-flow'
import { projectDefinitionEntrySearchSchema } from '@/features/projects/utils/search'

// The "Create new project" entry: a fullscreen flow, not a drawer. Nothing exists yet, so this
// route has no data to load — submitting basic data creates the Project and moves the flow onto
// the project-scoped `/projects/:id/definition` route.
export const Route = createFileRoute('/_authenticated/projects/definition')({
	validateSearch: projectDefinitionEntrySearchSchema,
	component: ProjectDefinitionEntryRoute,
})

function ProjectDefinitionEntryRoute() {
	const { t } = useTranslation()
	const search = Route.useSearch()
	const navigate = Route.useNavigate()

	const mode = search.mode
	const step = resolveDefinitionEntryStep(mode, search.step)
	const progress = mode ? getDefinitionFlowProgress(mode, step) : undefined

	return (
		<DefinitionFlowFrame
			title={t('projects.definition.title')}
			stepLabel={progress && t('projects.definition.progress', { current: progress.current, total: progress.total })}
			progress={progress}
			closeButton={
				<Button asChild variant='ghost' size='icon' aria-label={t('common.close')}>
					{/* Nothing is saved before basic data, so closing here goes back to the projects list. */}
					<Link to='/projects'>
						<X />
					</Link>
				</Button>
			}
		>
			{step === 'basic-data' && mode ? (
				<DefinitionBasicDataCreateStep
					backButton={<DefinitionFlowBackButton step='choice' />}
					onCreated={(project) => {
						navigate({
							to: '/projects/$projectId/definition',
							params: { projectId: project.id },
							search: { mode, step: getNextDefinitionFlowStep(mode, 'basic-data') ?? 'basic-data' },
						})
					}}
				/>
			) : (
				<DefinitionFlowChoiceStep selectedMode={mode} />
			)}
		</DefinitionFlowFrame>
	)
}
